import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { motion } from 'motion/react';
import {
  generateAdGroups,
  generateCustomGroupAdGroups,
  buildCSV,
  type GeneratedAdGroup,
  type FieldError,
  type FieldType,
  type ErrorType,
  type ExportOptions,
} from '@/lib/generator';
import type { TemplateSet, DataRow, CustomGroup, CampaignStructureOverride, BudgetConfig } from '@/types';
import {
  ArrowLeft, Download, AlertTriangle,
  Pencil, X, Check, LayoutGrid, Table2, FileCode2, SkipForward,
  CheckCircle2, RotateCcw,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

type ViewMode = 'cards' | 'table' | 'csv' | 'errors';

interface Generation {
  templateSet: TemplateSet;
  rows: DataRow[];
  customGroups: CustomGroup[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fieldKey(err: FieldError): string | null {
  // Use "a{N}:" prefix to match buildCSV override key format; only when adIndex is present (multi-ad)
  const adPrefix = err.adIndex != null ? `a${err.adIndex}:` : '';
  if (err.field === 'headline' && err.slotNumber != null) return `${adPrefix}h${err.slotNumber}`;
  if (err.field === 'description' && err.slotNumber != null) return `${adPrefix}d${err.slotNumber}`;
  if (err.field === 'keyword' && err.keywordId) return `kw_${err.keywordId}`;
  if (err.field === 'url') return `${adPrefix}url`;
  if (err.field === 'path1') return `${adPrefix}path1`;
  if (err.field === 'path2') return `${adPrefix}path2`;
  if (err.field === 'campaign') return 'campaign';
  if (err.field === 'ad_group') return 'ad_group';
  return null;
}

function getValueFromAg(ag: GeneratedAdGroup, key: string): string {
  if (key === 'campaign') return ag.campaignName;
  if (key === 'ad_group') return ag.adGroupName;

  // Parse ad-prefixed keys like "a2:h5" or "a1:url"
  const adMatch = key.match(/^a(\d+):(.+)$/);
  const adIndex = adMatch ? parseInt(adMatch[1]) : null;
  const innerKey = adMatch ? adMatch[2] : key;

  // Look up from the correct ad when multi-ad
  const ad = adIndex != null ? ag.ads?.find(a => a.adIndex === adIndex) : null;
  const headlines = ad ? ad.headlines : ag.headlines;
  const descriptions = ad ? ad.descriptions : ag.descriptions;

  if (innerKey === 'url') return ad ? ad.finalUrl : ag.finalUrl;
  if (innerKey === 'path1') return ad ? ad.path1 : ag.path1;
  if (innerKey === 'path2') return ad ? ad.path2 : ag.path2;
  if (innerKey.startsWith('h')) {
    const n = parseInt(innerKey.slice(1));
    return headlines.find(h => h.slotNumber === n)?.rawText ?? '';
  }
  if (innerKey.startsWith('d')) {
    const n = parseInt(innerKey.slice(1));
    return descriptions.find(d => d.slotNumber === n)?.rawText ?? '';
  }
  if (key.startsWith('kw_')) {
    return ag.keywords.find(k => `kw_${k.keywordId}` === key)?.text ?? '';
  }
  return '';
}

function fieldLabel(field: FieldType, slotNumber?: number, adIndex?: number): string {
  const adPrefix = adIndex != null ? `Ad ${adIndex} · ` : '';
  if (field === 'headline') return `${adPrefix}Headline ${slotNumber}`;
  if (field === 'description') return `${adPrefix}Description ${slotNumber}`;
  if (field === 'keyword') return 'Keyword';
  if (field === 'url') return `${adPrefix}Final URL`;
  if (field === 'path1') return 'Path 1';
  if (field === 'path2') return 'Path 2';
  if (field === 'campaign') return 'Campaign name';
  if (field === 'ad_group') return 'Ad group name';
  return field;
}

function getFieldLimit(key: string): number | null {
  // Strip ad prefix like "a2:" if present
  const inner = key.replace(/^a\d+:/, '');
  if (inner.startsWith('h')) return 30;
  if (inner.startsWith('d')) return 90;
  if (inner.startsWith('kw_')) return 80;
  return null;
}

const ERROR_TYPE_LABEL: Record<ErrorType, string> = {
  truncation: 'Over limit',
  missing_placeholder: 'Missing field',
  empty_slot: 'Empty slot',
};

// ─── TemplatePreviewPanel ─────────────────────────────────────────────────────

function TemplatePreviewPanel({
  templateSet,
  adGroups,
  isVisible,
  structureOverride,
  budgetConfig,
}: {
  templateSet: TemplateSet;
  adGroups: GeneratedAdGroup[];
  isVisible: boolean;
  structureOverride?: CampaignStructureOverride;
  budgetConfig?: BudgetConfig;
}) {
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [csvContent, setCsvContent] = useState('');
  const [csvBuilt, setCsvBuilt] = useState(false);
  const [skippedRows, setSkippedRows] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Map<string, Map<string, string>>>(new Map());
  const [editingAgId, setEditingAgId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Map<string, string>>(new Map());
  const [initialEditValues, setInitialEditValues] = useState<Map<string, string>>(new Map());

  const exportOptions: ExportOptions = useMemo(() => ({
    skippedHeadlineSlots: new Set<number>(),
    skippedDescriptionSlots: new Set<number>(),
    skippedRows,
    overrides,
  }), [skippedRows, overrides]);

  const isErrorResolved = useCallback((ag: GeneratedAdGroup, err: FieldError): boolean => {
    if (skippedRows.has(ag.rowId)) return true;
    const rowOv = overrides.get(ag.rowId);
    if (!rowOv) return false;
    const key = fieldKey(err);
    if (key == null || !rowOv.has(key)) return false;
    const val = rowOv.get(key)!;
    if (err.type === 'truncation') {
      const limit = getFieldLimit(key);
      return limit == null || val.length <= limit;
    }
    if (err.type === 'missing_placeholder') return !/\{[^}]+\}/.test(val);
    if (err.type === 'empty_slot') return val.trim().length > 0;
    return true;
  }, [skippedRows, overrides]);

  const erroredGroups = useMemo(() => adGroups.filter(ag => {
    if (ag.errors.length === 0) return false;
    if (skippedRows.has(ag.rowId)) return true;
    return ag.errors.some(e => {
      if (!isErrorResolved(ag, e)) return true;
      const key = fieldKey(e);
      return key != null && overrides.get(ag.rowId)?.has(key);
    });
  }), [adGroups, skippedRows, overrides, isErrorResolved]);

  const { totalActiveErrors, totalErroredCities } = useMemo(() => ({
    totalActiveErrors: adGroups.reduce(
      (sum, ag) => sum + ag.errors.filter(e => !isErrorResolved(ag, e)).length, 0,
    ),
    totalErroredCities: adGroups.filter(ag =>
      !skippedRows.has(ag.rowId) && ag.errors.some(e => !isErrorResolved(ag, e))
    ).length,
  }), [adGroups, skippedRows, overrides, isErrorResolved]);

  const campaignCount = useMemo(
    () => new Set(adGroups.map(ag => ag.campaignName)).size,
    [adGroups],
  );

  const exportableCount = adGroups.length - skippedRows.size;

  const campaignGroups = useMemo(() => {
    const map = new Map<string, {
      name: string;
      adGroups: GeneratedAdGroup[];
      isCustom: boolean;
      activeErrorCount: number;
    }>();
    adGroups.forEach(ag => {
      if (!map.has(ag.campaignName)) {
        map.set(ag.campaignName, {
          name: ag.campaignName,
          adGroups: [],
          isCustom: ag.id.startsWith('cg_'),
          activeErrorCount: 0,
        });
      }
      const g = map.get(ag.campaignName)!;
      g.adGroups.push(ag);
      g.activeErrorCount += ag.errors.filter(e => !isErrorResolved(ag, e)).length;
    });
    return Array.from(map.values());
  }, [adGroups, isErrorResolved]);

  // ── Actions ──

  const applyOverride = (rowId: string, key: string, value: string) => {
    setOverrides(prev => {
      const next = new Map(prev);
      if (!next.has(rowId)) next.set(rowId, new Map());
      next.get(rowId)!.set(key, value);
      return next;
    });
    setCsvBuilt(false);
  };

  const skipRow = (rowId: string) => {
    setSkippedRows(prev => { const next = new Set(prev); next.add(rowId); return next; });
    setCsvBuilt(false);
  };

  const unskipRow = (rowId: string) => {
    setSkippedRows(prev => { const next = new Set(prev); next.delete(rowId); return next; });
    setCsvBuilt(false);
  };

  const enterEditMode = (ag: GeneratedAdGroup) => {
    const vals = new Map<string, string>();
    ag.errors.forEach(err => {
      const key = fieldKey(err);
      if (key && !vals.has(key)) {
        const overrideVal = overrides.get(ag.rowId)?.get(key);
        vals.set(key, overrideVal !== undefined ? overrideVal : getValueFromAg(ag, key));
      }
    });
    setEditValues(vals);
    setInitialEditValues(new Map(vals));
    setEditingAgId(ag.id);
  };

  const confirmEditMode = (ag: GeneratedAdGroup) => {
    editValues.forEach((val, key) => {
      if (val !== initialEditValues.get(key)) applyOverride(ag.rowId, key, val);
    });
    setEditingAgId(null);
    setEditValues(new Map());
    setInitialEditValues(new Map());
  };

  const cancelEditMode = () => {
    setEditingAgId(null);
    setEditValues(new Map());
    setInitialEditValues(new Map());
  };

  const buildCsvNow = () => {
    const csv = buildCSV(adGroups, templateSet, exportOptions, structureOverride, budgetConfig);
    setCsvContent(csv);
    setCsvBuilt(true);
  };

  const handleExport = () => {
    const csv = buildCSV(adGroups, templateSet, exportOptions, structureOverride, budgetConfig);
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${templateSet.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded CSV - ${exportableCount.toLocaleString()} ad groups`);
  };

  return (
    <div style={{ display: isVisible ? undefined : 'none' }} className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {/* Stats */}
          <p className="text-sm text-muted-foreground">
            {campaignCount > 1 && `${campaignCount.toLocaleString()} campaigns · `}{adGroups.length.toLocaleString()} ad groups
            {skippedRows.size > 0 && ` · ${skippedRows.size} skipped`}
            {totalActiveErrors > 0 && (
              <span className="text-amber-600 dark:text-amber-400 ml-2">
                · {totalActiveErrors.toLocaleString()} issue{totalActiveErrors !== 1 ? 's' : ''} in {totalErroredCities.toLocaleString()} {totalErroredCities !== 1 ? 'cities' : 'city'}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* View mode tabs */}
          <div className="flex gap-1 p-1 bg-muted rounded-lg">
            {([
              { id: 'cards' as ViewMode, icon: LayoutGrid, label: 'Preview' },
              { id: 'table' as ViewMode, icon: Table2, label: 'Table' },
              { id: 'csv' as ViewMode, icon: FileCode2, label: 'CSV' },
              { id: 'errors' as ViewMode, icon: AlertTriangle, label: 'Issues', count: totalActiveErrors, cities: totalErroredCities, showBadge: totalActiveErrors > 0 },
            ] as Array<{ id: ViewMode; icon: React.ComponentType<{ className?: string }>; label: string; count?: number; cities?: number; showBadge?: boolean }>).map(({ id, icon: Icon, label, count, cities, showBadge }) => (
              <button
                key={id}
                onClick={() => setViewMode(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  viewMode === id
                    ? 'bg-card shadow text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className={`size-3.5 ${id === 'errors' && count ? 'text-amber-500' : ''}`} />
                {label}
                {showBadge && count != null && count > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">
                    {count.toLocaleString()}
                    {cities != null && <span className="opacity-70"> / {cities.toLocaleString()}</span>}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Export */}
          <Button onClick={handleExport} size="sm">
            <Download className="size-3.5 mr-1.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* ── Issues tab ── */}
      {viewMode === 'errors' && (
        <div className="space-y-3">
          {totalActiveErrors > 0 && (
            <p className="text-xs text-muted-foreground px-1">
              <span className="font-medium text-amber-600 dark:text-amber-400">{totalActiveErrors.toLocaleString()} issue{totalActiveErrors !== 1 ? 's' : ''}</span>
              {' across '}
              <span className="font-medium text-foreground">{totalErroredCities.toLocaleString()} {totalErroredCities !== 1 ? 'cities' : 'city'}</span>
              {' — '}avg {(totalActiveErrors / Math.max(totalErroredCities, 1)).toFixed(1)} per city
            </p>
          )}
          {erroredGroups.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
                <CheckCircle2 className="size-10 text-green-500" />
                <p className="font-medium">No issues — ready to export</p>
              </CardContent>
            </Card>
          ) : (
            erroredGroups.map(ag => {
              const isSkipped = skippedRows.has(ag.rowId);
              const isEditing = editingAgId === ag.id;
              const activeErrors = ag.errors.filter(e => !isErrorResolved(ag, e));
              const resolvedErrors = ag.errors.filter(e => {
                if (!isErrorResolved(ag, e)) return false;
                const key = fieldKey(e);
                return key != null && overrides.get(ag.rowId)?.has(key);
              });
              const displayErrors = [...activeErrors, ...resolvedErrors];

              return (
                <Card key={ag.id} className={`border-amber-500/20 ${isSkipped ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between px-5 py-3 border-b border-border/50">
                    <div>
                      <p className="text-xs text-muted-foreground">{ag.campaignName}</p>
                      <p className="text-sm font-semibold">{ag.adGroupName}</p>
                      {!isSkipped && activeErrors.length === 0 && resolvedErrors.length > 0 && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 flex items-center gap-1">
                          <CheckCircle2 className="size-3" /> All issues resolved
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {isSkipped ? (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground" onClick={() => unskipRow(ag.rowId)}>
                          <RotateCcw className="size-3 mr-1" />Undo skip
                        </Button>
                      ) : (
                        <>
                          {!isEditing && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-primary hover:text-primary hover:bg-primary/10" onClick={() => enterEditMode(ag)}>
                              <Pencil className="size-3 mr-1" />Edit row
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => skipRow(ag.rowId)}>
                            <SkipForward className="size-3 mr-1" />Skip row
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {isSkipped ? (
                    <div className="px-5 py-4 text-sm text-muted-foreground">This row will be excluded from the CSV export.</div>
                  ) : (
                    <div className="px-5 py-3 space-y-4">
                      {displayErrors.map((err, ei) => {
                        const key = fieldKey(err);
                        const overrideVal = key ? overrides.get(ag.rowId)?.get(key) : undefined;
                        const rawVal = key ? getValueFromAg(ag, key) : '';
                        const displayVal = overrideVal !== undefined ? overrideVal : rawVal;
                        const isSkippedField = overrideVal === '';
                        const limit = key ? getFieldLimit(key) : null;
                        const isFit = overrideVal != null && overrideVal !== '' && (limit == null || overrideVal.length <= limit);
                        const fieldEditVal = key ? (editValues.get(key) ?? displayVal) : displayVal;

                        const borderColor = isSkippedField
                          ? 'border-muted-foreground/25'
                          : isFit ? 'border-green-500/50' : 'border-amber-400/50';

                        return (
                          <div key={ei} className={`pl-3 border-l-2 ${borderColor} space-y-1.5`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium">{fieldLabel(err.field, err.slotNumber, err.adIndex)}</span>
                              {isSkippedField ? (
                                <Badge variant="outline" className="text-xs text-muted-foreground">Skipped</Badge>
                              ) : isFit ? (
                                <Badge variant="outline" className="text-xs text-green-700 dark:text-green-400 border-green-500/40 bg-green-500/5">
                                  <Check className="size-2.5 mr-1" />Fixed
                                </Badge>
                              ) : (
                                <>
                                  <Badge variant="outline" className="text-xs text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5">
                                    {ERROR_TYPE_LABEL[err.type] ?? err.type}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">{err.message}</span>
                                </>
                              )}
                            </div>
                            {isEditing && key ? (
                              <div className="space-y-1">
                                <Input
                                  value={fieldEditVal}
                                  onChange={e => setEditValues(prev => new Map(prev).set(key, e.target.value))}
                                  className="h-7 text-xs font-mono"
                                  placeholder="Replacement value for this row…"
                                />
                                {limit && (
                                  <p className={`text-xs ${fieldEditVal.length > limit ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                                    {fieldEditVal.length}/{limit} chars
                                    {fieldEditVal.length > limit && ` — ${fieldEditVal.length - limit} over`}
                                  </p>
                                )}
                              </div>
                            ) : (
                              displayVal && !isSkippedField && (
                                <p className={`text-xs font-mono rounded px-2 py-1 truncate max-w-prose ${
                                  isFit
                                    ? 'text-green-700 dark:text-green-400 bg-green-500/5 border border-green-500/20'
                                    : 'text-foreground/70 bg-muted/50'
                                }`}>
                                  "{displayVal}"
                                </p>
                              )
                            )}
                          </div>
                        );
                      })}
                      {isEditing && (
                        <div className="flex gap-2 pt-2 border-t border-border/50">
                          <Button size="sm" className="h-7 text-xs" onClick={() => confirmEditMode(ag)}>
                            <Check className="size-3 mr-1" />Apply
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancelEditMode}>
                            <X className="size-3 mr-1" />Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* ── Preview tab — one sample per campaign ── */}
      {viewMode === 'cards' && (
        <div className="space-y-3">
          {campaignGroups.map(campaign => {
            const sampleAg = campaign.adGroups.find(ag => !skippedRows.has(ag.rowId)) ?? campaign.adGroups[0];
            const rowOv = overrides.get(sampleAg.rowId) ?? new Map<string, string>();
            const ov = (key: string, fallback: string) => rowOv.get(key) ?? fallback;
            const activeErrors = sampleAg.errors.filter(e => !isErrorResolved(sampleAg, e));

            return (
              <div key={campaign.name} className="space-y-1.5">
                <div className="flex items-center gap-2 px-1">
                  {campaign.isCustom ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-violet-500/10 border-violet-500/30 text-violet-600 dark:text-violet-400 shrink-0">Custom</span>
                  ) : (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-primary/8 border-primary/20 text-primary/70 shrink-0">Bulk</span>
                  )}
                  <span className="text-sm font-semibold truncate">{campaign.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                    {campaign.adGroups.length.toLocaleString()} ad group{campaign.adGroups.length !== 1 ? 's' : ''}
                  </span>
                  {campaign.activeErrorCount > 0 && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/40 shrink-0">
                      {campaign.activeErrorCount} issue{campaign.activeErrorCount !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>

                <Card className={`border-l-[3px] ${campaign.isCustom ? 'border-l-violet-400/60' : 'border-l-primary/25'}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[10px] text-muted-foreground/60 uppercase tracking-widest font-mono mb-0.5">sample</p>
                        <p className="font-semibold text-sm">{ov('ad_group', sampleAg.adGroupName)}</p>
                      </div>
                      {activeErrors.length > 0 && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/40 shrink-0">
                          {activeErrors.length} issue{activeErrors.length !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Google Ads mockup */}
                    {(() => {
                      const adHeadlines = sampleAg.headlines.map(h => ov(`h${h.slotNumber}`, h.text)).filter(Boolean);
                      const adDescriptions = sampleAg.descriptions.map(d => ov(`d${d.slotNumber}`, d.text)).filter(Boolean);
                      const finalUrl = ov('url', sampleAg.finalUrl);
                      const path1 = ov('path1', sampleAg.path1);
                      const path2 = ov('path2', sampleAg.path2);
                      const displayUrl = (() => {
                        if (!finalUrl) return '';
                        try {
                          const u = new URL(finalUrl);
                          const paths = [path1, path2].filter(Boolean);
                          return `${u.hostname}${paths.length ? ' › ' + paths.join(' › ') : ''}`;
                        } catch { return finalUrl; }
                      })();
                      return (
                        <div className="bg-white dark:bg-[#1c1c2e] border border-[#dadce0] dark:border-[#2a2a4a] rounded-xl p-4 shadow-sm">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[11px] border border-[#006621] dark:border-[#4caf50] text-[#006621] dark:text-[#4caf50] rounded px-1 leading-tight font-medium">Ad</span>
                            <span className="text-xs text-[#202124] dark:text-[#9aa0a6] truncate">
                              {displayUrl || <span className="italic opacity-40">No URL</span>}
                            </span>
                          </div>
                          <div className="text-[#1a0dab] dark:text-[#8ab4f8] text-[17px] font-normal leading-tight mb-1.5 font-sans">
                            {adHeadlines.length > 0
                              ? adHeadlines.slice(0, 3).join(' | ')
                              : <span className="italic opacity-40 text-base">No headlines</span>}
                          </div>
                          {adDescriptions.length > 0
                            ? adDescriptions.slice(0, 2).map((d, i) => (
                              <p key={i} className="text-sm text-[#4d5156] dark:text-[#bdc1c6] leading-snug">{d}</p>
                            ))
                            : <p className="text-sm italic opacity-40">No descriptions</p>}
                        </div>
                      );
                    })()}

                    {/* Headlines */}
                    <div>
                      <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest mb-1">Headlines</p>
                      <div className="space-y-0.5">
                        {sampleAg.headlines.map(h => {
                          const val = ov(`h${h.slotNumber}`, h.text);
                          if (!val && !rowOv.has(`h${h.slotNumber}`)) return null;
                          const hasError = activeErrors.some(e => e.field === 'headline' && e.slotNumber === h.slotNumber);
                          return (
                            <div key={h.slotNumber} className={`flex items-baseline gap-2 text-xs ${hasError ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                              <span className="text-muted-foreground/50 w-5 shrink-0 font-mono">H{h.slotNumber}</span>
                              <span className="font-mono">{val || <span className="italic text-muted-foreground/40">empty</span>}</span>
                              {val && <span className="text-muted-foreground/40 shrink-0 tabular-nums">{val.length}/30</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Descriptions */}
                    <div>
                      <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest mb-1">Descriptions</p>
                      <div className="space-y-0.5">
                        {sampleAg.descriptions.map(d => {
                          const val = ov(`d${d.slotNumber}`, d.text);
                          if (!val && !rowOv.has(`d${d.slotNumber}`)) return null;
                          const hasError = activeErrors.some(e => e.field === 'description' && e.slotNumber === d.slotNumber);
                          return (
                            <div key={d.slotNumber} className={`flex items-baseline gap-2 text-xs ${hasError ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                              <span className="text-muted-foreground/50 w-5 shrink-0 font-mono">D{d.slotNumber}</span>
                              <span className="font-mono">{val || <span className="italic text-muted-foreground/40">empty</span>}</span>
                              {val && <span className="text-muted-foreground/40 shrink-0 tabular-nums">{val.length}/90</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Keywords */}
                    {sampleAg.keywords.length > 0 && (
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest mb-1">Keywords</p>
                        <div className="flex flex-wrap gap-1">
                          {sampleAg.keywords.slice(0, 6).map(kw => (
                            <Badge key={kw.keywordId} variant="secondary" className="text-xs font-mono">
                              {ov(`kw_${kw.keywordId}`, kw.text)}
                            </Badge>
                          ))}
                          {sampleAg.keywords.length > 6 && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">+{sampleAg.keywords.length - 6} more</Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* URL */}
                    {ov('url', sampleAg.finalUrl) && (
                      <p className="text-xs text-muted-foreground/60 font-mono truncate">{ov('url', sampleAg.finalUrl)}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Table view ── */}
      {viewMode === 'table' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Campaign</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Ad Group</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Headlines</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Keywords</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {adGroups.slice(0, 200).map((ag, idx) => {
                    const isSkipped = skippedRows.has(ag.rowId);
                    const activeErrors = ag.errors.filter(e => !isErrorResolved(ag, e));
                    const prevCampaign = idx > 0 ? adGroups[idx - 1].campaignName : null;
                    const showCampaignHeader = campaignCount > 1 && ag.campaignName !== prevCampaign;
                    return (
                      <Fragment key={ag.id}>
                        {showCampaignHeader && (
                          <tr className="bg-muted/50 border-b border-t">
                            <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              {ag.campaignName}
                            </td>
                          </tr>
                        )}
                        <tr className={`border-b last:border-0 ${isSkipped ? 'opacity-40' : ''} ${activeErrors.length ? 'bg-amber-500/5' : ''}`}>
                          <td className="px-4 py-2 text-xs text-muted-foreground max-w-[160px] truncate">{ag.campaignName}</td>
                          <td className="px-4 py-2 font-medium max-w-[160px] truncate">{ag.adGroupName}</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">{ag.headlines.filter(h => h.text).length}/15 filled</td>
                          <td className="px-4 py-2 text-xs">{ag.keywords.length} kw</td>
                          <td className="px-4 py-2">
                            {activeErrors.length > 0 && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/40">{activeErrors.length}</Badge>
                            )}
                            {isSkipped && <Badge variant="outline" className="text-xs text-muted-foreground">Skipped</Badge>}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {adGroups.length > 200 && (
              <p className="text-xs text-muted-foreground px-4 py-3">
                Showing first 200 of {adGroups.length.toLocaleString()} ad groups · Export CSV to see all
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── CSV view ── */}
      {viewMode === 'csv' && (
        <Card>
          <CardContent className="space-y-3 pt-5">
            {!csvBuilt ? (
              <div className="flex flex-col items-center gap-3 py-8">
                <p className="text-sm text-muted-foreground">Build a preview of the CSV with your current settings.</p>
                <Button variant="outline" onClick={buildCsvNow}>
                  <FileCode2 className="size-4 mr-2" />Build CSV Preview
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Showing first 20 rows of {csvContent.split('\n').length.toLocaleString()} total lines
                  </p>
                  <Button
                    variant="ghost" size="sm" className="text-xs h-7"
                    onClick={() => { navigator.clipboard.writeText(csvContent); toast.success('Copied to clipboard'); }}
                  >
                    Copy all
                  </Button>
                </div>
                <pre className="text-xs font-mono bg-muted/50 rounded-md p-3 overflow-x-auto max-h-[400px] overflow-y-auto leading-5">
                  {csvContent.split('\n').slice(0, 21).join('\n')}
                </pre>
                <Button variant="outline" size="sm" className="text-xs" onClick={buildCsvNow}>Rebuild preview</Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function GenerationPreviewView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();

  // Normalize old single-template state and new multi-template state
  const rawState = location.state as
    | { generations: Array<{ templateSet: TemplateSet; rows: DataRow[]; customGroups?: CustomGroup[] }>; structureOverride?: CampaignStructureOverride; budgetConfig?: BudgetConfig }
    | { templateSet: TemplateSet; rows: DataRow[]; customGroups?: CustomGroup[] }
    | null;

  const generations: Generation[] | null = rawState == null ? null :
    'generations' in rawState
      ? rawState.generations.map(g => ({ ...g, customGroups: g.customGroups ?? [] }))
      : [{ templateSet: rawState.templateSet, rows: rawState.rows, customGroups: rawState.customGroups ?? [] }];

  const structureOverride: CampaignStructureOverride | undefined =
    rawState && 'generations' in rawState ? rawState.structureOverride : undefined;
  const budgetConfig: BudgetConfig | undefined =
    rawState && 'generations' in rawState ? rawState.budgetConfig : undefined;

  useEffect(() => {
    if (!generations) navigate(`/projects/${projectId}/generate`, { replace: true });
  }, []);

  const [allAdGroups, setAllAdGroups] = useState<GeneratedAdGroup[][]>([]);
  const [isProcessing, setIsProcessing] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!generations) return;
    const MIN_MS = 1600;
    const start = Date.now();
    const results = generations.map(gen => {
      if (structureOverride) {
        // When structure override is active, merge all rows (bulk + custom group)
        // and run through generateAdGroups which respects the grouping field
        const allRows = [
          ...gen.customGroups.flatMap(cg => cg.rows),
          ...gen.rows,
        ];
        return allRows.length > 0 ? generateAdGroups(gen.templateSet, allRows, structureOverride) : [];
      }
      const bulk = gen.rows.length > 0 ? generateAdGroups(gen.templateSet, gen.rows) : [];
      const custom = gen.customGroups.length > 0 ? generateCustomGroupAdGroups(gen.templateSet, gen.customGroups) : [];
      return [...custom, ...bulk];
    });
    const elapsed = Date.now() - start;
    const tid = setTimeout(() => {
      setAllAdGroups(results);
      setIsProcessing(false);
    }, Math.max(0, MIN_MS - elapsed));
    return () => clearTimeout(tid);
  }, []);

  if (!generations) return null;

  // ── Loading screen ──
  if (isProcessing) {
    const totalRows = generations.reduce((s, g) => s + g.rows.length + g.customGroups.reduce((a, cg) => a + cg.rows.length, 0), 0);
    return (
      <motion.div
        className="flex flex-col items-center justify-center min-h-[60vh] gap-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="relative flex items-center justify-center">
          <motion.div
            className="size-20 border-2 border-primary/15 border-t-primary rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute size-12 border-2 border-primary/10 border-b-primary/50 rounded-full"
            animate={{ rotate: -360 }}
            transition={{ duration: 1.7, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute size-4 rounded-full bg-primary/70"
            animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2.2, repeat: Infinity }}
          />
        </div>
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <p className="font-display font-semibold text-xl mb-1">Generating ad copy</p>
          <p className="text-muted-foreground text-sm">
            {generations.length > 1
              ? `Processing ${generations.length} template sets · ${totalRows.toLocaleString()} rows…`
              : `Processing ${totalRows.toLocaleString()} rows…`}
          </p>
        </motion.div>
      </motion.div>
    );
  }

  const multiTemplate = generations.length > 1;

  return (
    <motion.div
      className="space-y-4"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}/generate`)}>
          <ArrowLeft className="size-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {multiTemplate ? `${generations.length} Template Sets` : generations[0].templateSet.name}
          </h1>
          {multiTemplate && (
            <p className="text-sm text-muted-foreground">
              {allAdGroups.reduce((s, ags) => s + ags.length, 0).toLocaleString()} total ad groups across {generations.length} templates
            </p>
          )}
        </div>
        {multiTemplate && allAdGroups.length > 0 && (
          <Button
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() => {
              const csvParts: string[] = [];
              generations.forEach((gen, idx) => {
                const ags = allAdGroups[idx] ?? [];
                if (ags.length === 0) return;
                const csv = buildCSV(ags, gen.templateSet, {
                  skippedRows: new Set(),
                  overrides: new Map(),
                  skippedHeadlineSlots: new Set(),
                  skippedDescriptionSlots: new Set(),
                }, structureOverride, budgetConfig);
                const lines = csv.split('\n');
                if (csvParts.length === 0) {
                  csvParts.push(...lines);
                } else {
                  csvParts.push(...lines.slice(1));
                }
              });
              const combined = csvParts.join('\n');
              const blob = new Blob(['\uFEFF' + combined], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `combined-${generations.length}-templates-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              const totalAgs = allAdGroups.reduce((s, a) => s + a.length, 0);
              toast.success(`Downloaded combined CSV - ${totalAgs.toLocaleString()} ad groups from ${generations.length} templates`);
            }}
          >
            <Download className="size-3.5" />
            Export All ({allAdGroups.reduce((s, a) => s + a.length, 0).toLocaleString()})
          </Button>
        )}
      </div>

      {/* ── Template tabs (multi only) ── */}
      {multiTemplate && (
        <div className="flex gap-0 border-b border-border overflow-x-auto">
          {generations.map((gen, idx) => {
            const ags = allAdGroups[idx] ?? [];
            const errCount = ags.reduce((sum, ag) => sum + ag.errors.length, 0);
            return (
              <button
                key={gen.templateSet.id}
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  activeIdx === idx
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {gen.templateSet.name}
                <span className="text-[10px] font-mono text-muted-foreground/60">{gen.templateSet.language_code.toUpperCase()}</span>
                {errCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">
                    {errCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Panels — all rendered, inactive hidden ── */}
      {generations.map((gen, idx) => (
        <TemplatePreviewPanel
          key={gen.templateSet.id}
          templateSet={gen.templateSet}
          adGroups={allAdGroups[idx] ?? []}
          isVisible={activeIdx === idx}
          structureOverride={structureOverride}
          budgetConfig={budgetConfig}
        />
      ))}
    </motion.div>
  );
}
